// The targeting scope that decides whether two MOI email templates compete for the same MOI method.
// The backend keys method mappings by scope, so a method is only "already mapped elsewhere" when
// another non-Default template shares every scope dimension (sub-orgs / LOBs / coverages / countries
// / states). Enable/active state is deliberately not part of the scope.
export interface MoiEmailScope {
  is_all_suborgs?: boolean | null;
  sub_organization_ids?: number[] | null;
  is_all_lobs?: boolean | null;
  lobs?: string[] | null;
  is_all_coverages?: boolean | null;
  coverage_keys?: string[] | null;
  is_all_countries?: boolean | null;
  countries?: string[] | null;
  is_all_states?: boolean | null;
  states?: string[] | null;
}

// A single dimension collapses to "ALL" when its is_all_* flag is set; otherwise to its member list
// normalized to a sorted, order-independent token (null/undefined lists behave like an empty list).
const dimensionToken = (
  isAll: boolean | null | undefined,
  values: readonly (string | number)[] | null | undefined
): string => (isAll ? 'ALL' : [...(values ?? [])].map(String).sort().join(','));

export const moiEmailScopeKey = (scope: MoiEmailScope): string =>
  [
    `suborgs:${dimensionToken(scope.is_all_suborgs, scope.sub_organization_ids)}`,
    `lobs:${dimensionToken(scope.is_all_lobs, scope.lobs)}`,
    `coverages:${dimensionToken(scope.is_all_coverages, scope.coverage_keys)}`,
    `countries:${dimensionToken(scope.is_all_countries, scope.countries)}`,
    `states:${dimensionToken(scope.is_all_states, scope.states)}`,
  ].join(';');

export const moiEmailScopesMatch = (a: MoiEmailScope, b: MoiEmailScope): boolean =>
  moiEmailScopeKey(a) === moiEmailScopeKey(b);

// Which target dimensions the builder form actually shows — sub-orgs / countries / states selectors
// are org-dependent (sub_organizations_enabled / multi-country / org-with-states). A hidden
// dimension can't be picked, so it never counts against target completeness.
export interface MoiEmailTargetDimensionsShown {
  subOrganizations: boolean;
  countries: boolean;
  states: boolean;
}

const isDimensionSelected = (
  isAll: boolean | null | undefined,
  values: readonly (string | number)[] | null | undefined
): boolean => !!isAll || (values?.length ?? 0) > 0;

// True when every shown target dimension is either "ALL" or carries at least one explicit pick.
// The methods picker is gated on this: method-conflict detection is keyed by the full scope
// (moiEmailScopeKey), so offering methods before the target is set lets the user pick methods
// that are already mapped under the scope they end up choosing.
export const isMoiEmailTargetFullySelected = (scope: MoiEmailScope, shown: MoiEmailTargetDimensionsShown): boolean =>
  (!shown.subOrganizations || isDimensionSelected(scope.is_all_suborgs, scope.sub_organization_ids)) &&
  isDimensionSelected(scope.is_all_lobs, scope.lobs) &&
  isDimensionSelected(scope.is_all_coverages, scope.coverage_keys) &&
  (!shown.countries || isDimensionSelected(scope.is_all_countries, scope.countries)) &&
  (!shown.states || isDimensionSelected(scope.is_all_states, scope.states));
